// Helpers for rendering LinkedIn post embeds.
//
// The list of posts lives in ONE place: the `LINKEDIN_POST_URLS` array at the
// top of `src/components/LinkedInCarousel.tsx`. Each entry is just a LinkedIn
// post URL — the component builds the iframe from it.

export type LinkedInPost = {
  /** Full LinkedIn embed iframe `src` URL (derived from the post URL). */
  embedUrl: string;
  /** Iframe height in pixels. Defaults to 600. */
  height?: number;
  /** Optional short label shown above the embed. */
  caption?: string;
  /** Optional 1–2 sentence summary rendered above the iframe. */
  summary?: string;
};

/**
 * Accepts any LinkedIn post URL form and returns the embed iframe `src`:
 *  - https://www.linkedin.com/feed/update/urn:li:share:123
 *  - https://www.linkedin.com/posts/slug-activity-123-abcd
 *  - https://www.linkedin.com/embed/feed/update/urn:li:activity:123 (passthrough)
 */
export function toEmbedUrl(postUrl: string): string {
  const trimmed = postUrl.trim();

  // Already an embed URL.
  if (trimmed.includes("/embed/feed/update/")) {
    return withCollapsed(trimmed);
  }

  // Any URL containing a urn (feed/update form).
  const urnMatch = trimmed.match(/urn:li:(?:share|activity|ugcPost):\d+/);
  if (urnMatch) {
    return withCollapsed(
      `https://www.linkedin.com/embed/feed/update/${urnMatch[0]}`,
    );
  }

  // Vanity permalink: /posts/<slug>-activity-<id>-<hash>
  const activityMatch = trimmed.match(/activity[-:](\d{10,})/);
  if (activityMatch) {
    return withCollapsed(
      `https://www.linkedin.com/embed/feed/update/urn:li:activity:${activityMatch[1]}`,
    );
  }

  return trimmed;
}

function withCollapsed(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("collapsed", "1");
    return parsed.toString();
  } catch {
    return url.includes("?") ? url : `${url}?collapsed=1`;
  }
}

/** Builds carousel post objects from a plain list of LinkedIn post URLs. */
export function postsFromUrls(urls: readonly string[]): LinkedInPost[] {
  return urls
    .map((url) => url.trim())
    .filter(Boolean)
    .map((url) => ({ embedUrl: toEmbedUrl(url) }));
}
